import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./_lazyAssetImage.scss";

// Bu oturumda tam yüklenmiş URL'ler: filtre/sekme değişip hücre yeniden mount
// olunca görsel tarayıcı cache'inden gelir, skeleton + fade tekrar oynamaz.
const loadedUrls = new Set();
export const __resetLoadedCache = () => loadedUrls.clear();

// En yakın kaydırılabilir ata → IntersectionObserver root'u. Panel kendi içinde
// kaydığı için viewport yerine onu root alırız; rootMargin böylece panel içinde
// "biraz önceden yükle" anlamına gelir.
const findScrollParent = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
};

const LazyAssetImage = ({ src, alt, fallback, rootMargin = "300px", style }) => {
  const holderRef = useRef(null);
  const alreadyLoaded = loadedUrls.has(src);
  const [inView, setInView] = useState(
    alreadyLoaded || typeof window === "undefined" || !window.IntersectionObserver
  );
  const [loaded, setLoaded] = useState(alreadyLoaded);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (inView) return undefined;
    const holder = holderRef.current;
    if (!holder) return undefined;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { root: findScrollParent(holder), rootMargin }
    );
    observer.observe(holder);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  if (failed && fallback) return fallback;

  return (
    <div ref={holderRef} className="cmp-lazyimg" style={style}>
      {!loaded ? <div className="cmp-lazyimg__skeleton" aria-hidden="true" /> : null}
      {inView ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`cmp-lazyimg__img${loaded ? " is-loaded" : ""}`}
          onLoad={() => {
            loadedUrls.add(src);
            setLoaded(true);
          }}
          onError={() => setFailed(true)}
        />
      ) : null}
    </div>
  );
};

LazyAssetImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  fallback: PropTypes.node,
  rootMargin: PropTypes.string,
  style: PropTypes.object,
};

export default LazyAssetImage;
